import React from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import {
  CheckCircleOutline,
  ErrorOutline,
  InfoOutlined,
  LockOutlined,
  Schedule,
} from "@mui/icons-material";

const CLIENT_NAMES = {
  "app.mhome.ai": "MeowLink",
  "console.mhome.ai": "MeowLink Console",
  "desktop.mhome.ai": "MeowLink Desktop",
  "mobile.mhome.ai": "MeowLink Mobile",
};
const LEGACY_NAMES = {
  mhome: "MeowLink",
  "mhome console": "MeowLink Console",
  "mhome desktop": "MeowLink Desktop",
  "mhome mobile": "MeowLink Mobile",
};

export function loginRequesterName(clientId, displayName) {
  if (CLIENT_NAMES[clientId]) return CLIENT_NAMES[clientId];
  const name = (displayName || "").trim();
  if (LEGACY_NAMES[name.toLowerCase()]) return LEGACY_NAMES[name.toLowerCase()];
  // Older servers may omit the client ID. Never show legacy branding or
  // internal domains as the fallback requester label.
  if (!name || /mhome|the requesting application/i.test(name))
    return "Your app";
  return name;
}

export function loginNotice({
  notice,
  busy,
  progressTitle,
  loading,
  pairing,
  requester,
  challenge,
  reset,
  signingUp,
  delivery,
  expiring,
  remaining,
}) {
  let result;
  if (remaining === 0) {
    return {
      kind: "warning",
      title: "This sign-in request has expired",
      message: "Return to your app and start a new sign-in request.",
    };
  }
  if (notice?.kind === "error") {
    result = {
      kind: "error",
      title: "Let's try that again",
      message: notice.message,
    };
  } else if (busy || loading) {
    result = {
      kind: "progress",
      title: loading
        ? "Checking your sign-in request"
        : delivery
          ? "Connecting to your app"
          : pairing
            ? "Checking your pairing code"
            : challenge
              ? "Verifying your response"
              : progressTitle || "Signing you in",
      message: "Please keep this page open.",
    };
  } else if (notice?.message) {
    result = {
      kind: "success",
      title: "You're ready for the next step",
      message: notice.message,
    };
  } else if (challenge) {
    result = { kind: "info", title: "Verify your sign-in", message: challenge };
  } else if (pairing) {
    result = {
      kind: "info",
      title: "Connect your app",
      message:
        "Enter the pairing code shown in the app that requested this sign-in.",
    };
  } else {
    result = {
      kind: "neutral",
      title: reset
        ? "Reset your password"
        : signingUp
          ? "Create your account"
          : "Sign in to continue",
      message: `${requester} requested this sign-in.`,
    };
  }
  if (expiring) {
    const time = `${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, "0")}`;
    result.expiry = `This request expires in ${time}.`;
    if (result.kind === "neutral") result.kind = "warning";
  }
  return result;
}

const STYLES = {
  neutral: { color: "#46636b", background: "#f1f7f7", Icon: LockOutlined },
  progress: { color: "#46636b", background: "#f1f7f7", Icon: LockOutlined },
  info: { color: "#426775", background: "#eef6f9", Icon: InfoOutlined },
  success: {
    color: "#367358",
    background: "#edf7f0",
    Icon: CheckCircleOutline,
  },
  error: { color: "#a34747", background: "#fff1ef", Icon: ErrorOutline },
  warning: { color: "#86602b", background: "#fff7e9", Icon: Schedule },
};

export default function UserLoginNotice({ value }) {
  const { color, background, Icon } = STYLES[value.kind] || STYLES.neutral;
  return (
    <Box
      data-testid="login-notice"
      sx={{
        display: "flex",
        gap: 1.5,
        alignItems: "flex-start",
        p: 2,
        minHeight: 76,
        boxSizing: "border-box",
        borderRadius: "14px",
        bgcolor: background,
        transition: "background-color 160ms ease",
        "@media (prefers-reduced-motion: reduce)": { transition: "none" },
      }}
    >
      <Box
        aria-hidden="true"
        sx={{ color, display: "flex", mt: 0.2, flexShrink: 0 }}
      >
        {value.kind === "progress" ? (
          <CircularProgress size={20} color="inherit" />
        ) : (
          <Icon sx={{ fontSize: 21 }} />
        )}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box
          role={value.kind === "error" ? "alert" : "status"}
          aria-atomic="true"
        >
          <Typography
            sx={{ color, fontSize: 14, fontWeight: 650, lineHeight: 1.4 }}
          >
            {value.title}
          </Typography>
          <Typography
            sx={{
              mt: 0.4,
              color: "#52666c",
              fontSize: 13,
              lineHeight: 1.55,
              overflowWrap: "anywhere",
            }}
          >
            {value.message}
          </Typography>
        </Box>
        {value.expiry && (
          <Typography
            sx={{ mt: 0.75, color: "#86602b", fontSize: 12, lineHeight: 1.5 }}
          >
            {value.expiry}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
